import * as Sentry from '@sentry/react'
import type { ErrorInfo } from 'react-dom/client'

type ReactRecoverableErrorKind =
  | 'hydration_mismatch'
  | 'server_suspense_fallback'
  | 'suspense_hydration_error'
  | 'boundary_updated_during_hydration'
  | 'boundary_recovery'
  | 'root_recovery'
  | 'root_updated_during_hydration'
  | 'text_mismatch'
  | 'other'

const recoverableErrorKindsByCode: Partial<Record<string, ReactRecoverableErrorKind>> = {
  '418': 'hydration_mismatch',
  '419': 'server_suspense_fallback',
  '420': 'suspense_hydration_error',
  '421': 'boundary_updated_during_hydration',
  '422': 'boundary_recovery',
  '423': 'root_recovery',
  '424': 'root_updated_during_hydration',
  '425': 'text_mismatch',
}

const genericRecoveryKinds = new Set<ReactRecoverableErrorKind>([
  'boundary_recovery',
  'root_recovery',
])

const getErrorMessage = (error: unknown): string => error instanceof Error ? error.message : ''

const getReactErrorCode = (message: string): string | undefined =>
  message.match(/Minified React error #(\d+)/)?.[1] ??
  message.match(/(?:errors\/|invariant=)(\d+)/)?.[1]

const getRecoverableErrorKind = (message: string, code?: string): ReactRecoverableErrorKind => {
  const codedKind = code === undefined ? undefined : recoverableErrorKindsByCode[code]

  if (codedKind !== undefined) {
    return codedKind
  }

  if (message.includes('Text content does not match server-rendered HTML')) {
    return 'text_mismatch'
  }

  if (message.includes('server could not finish this Suspense boundary')) {
    return 'server_suspense_fallback'
  }

  if (message.includes('Suspense boundary received an update before it finished hydrating')) {
    return 'boundary_updated_during_hydration'
  }

  if (message.includes('root received an early update')) {
    return 'root_updated_during_hydration'
  }

  if (message.includes('nearest Suspense boundary')) {
    return 'boundary_recovery'
  }

  if (message.includes('client rendering the entire root')) {
    return 'root_recovery'
  }

  if (message.includes('error while hydrating this Suspense boundary')) {
    return 'suspense_hydration_error'
  }

  if (message.includes('Hydration failed')) {
    return 'hydration_mismatch'
  }

  return 'other'
}

const getTopComponent = (componentStack?: string): string | undefined => {
  for (const frame of componentStack?.split('\n') ?? []) {
    const component = frame.match(/^\s*at\s+([^(]+)/)?.[1]?.trim()

    if (component && /^[A-Z]/.test(component)) {
      return component
    }
  }

  return undefined
}

export default function createReactRecoverableErrorHandler(reactRoot: string) {
  const capturedErrors = new Set<string>()
  let capturedSpecificHydrationError = false

  return (error: unknown, errorInfo: ErrorInfo) => {
    const message = getErrorMessage(error)
    const code = getReactErrorCode(message)
    const kind = getRecoverableErrorKind(message, code)
    const component = getTopComponent(errorInfo.componentStack)
    const dedupeKey = `${kind}:${component ?? 'unknown'}`

    if (genericRecoveryKinds.has(kind) && capturedSpecificHydrationError) {
      return
    }

    if (capturedErrors.has(dedupeKey)) {
      return
    }

    capturedErrors.add(dedupeKey)

    if (!genericRecoveryKinds.has(kind) && kind !== 'other') {
      capturedSpecificHydrationError = true
    }

    Sentry.withScope(scope => {
      scope.setTag('react_error_source', 'onRecoverableError')
      scope.setTag('react_root', reactRoot)
      scope.setTag('react_recoverable_kind', kind)

      if (code) {
        scope.setTag('react_error_code', code)
      }

      if (component) {
        scope.setTag('react_component', component.slice(0, 200))
      }

      scope.setContext('react_recoverable_error', {
        componentStack: errorInfo.componentStack?.slice(0, 8000) ?? null,
        digest: errorInfo.digest ?? null,
      })
      scope.setFingerprint([
        'react-recoverable-error',
        kind,
        reactRoot,
        component ?? 'unknown',
      ])

      Sentry.captureException(
        error instanceof Error ? error : new Error('Unknown React recoverable error'),
        {
          mechanism: {
            type: 'react.onRecoverableError',
            handled: true,
          },
        },
      )
    })
  }
}
